import React from "react";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import {
  useTranslate,
  useListContext,
  Filter,
  TextInput,
  Datagrid,
  TextField,
  FunctionField,
  Record,
  CreateButton,
  SimpleList,
  Responsive,
} from "react-admin";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import NotificationsOutlinedIcon from "@material-ui/icons/NotificationsOutlined";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";
import { Box, Theme, useTheme, Tooltip } from "@material-ui/core";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { NotificationSubscriptionListDto } from "../../../common/data/service-proxies";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import NotificationImportantIcon from "@material-ui/icons/NotificationImportant";
import EmailIcon from "@material-ui/icons/Email";
import SmsIcon from "@material-ui/icons/Sms";

export const NotificationSubscriptionsList = (props: any) => {
  const translate = useTranslate();
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.NotificationSubscription,
    (x) => x.list
  );

  const ListFilter = (props: any) => {
    useSearchFields(props, ["name"]);
    const { loading } = useListContext();
    return (
      <Filter {...props}>
        {!loading && (
          <TextInput
            className="groups-list-filter search-box"
            label="resources.common.labels.search"
            source="q"
            alwaysOn
            autoFocus
            autoComplete="off"
          />
        )}
        {loading && (
          <TextInput
            className="groups-list-filter search-box"
            label="resources.common.labels.search"
            source="q"
            alwaysOn
          />
        )}
      </Filter>
    );
  };

  const ListActions = () => {
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <>
        {permissions &&
          permissions[permissionNames.admin.notifications.main] &&
          permissions[permissionNames.admin.notifications.create] && (
            <CreateButton basePath={basePath} />
          )}
      </>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Tooltip title="Name">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <NotificationImportantIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="name">
              <TextField
                source="name"
                label="resources.admin-notifications.labels.name"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <FunctionField<NotificationSubscriptionListDto & Record>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color: theme.palette.error.main,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Tooltip title="Email">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <EmailIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="emailRecipients">
              <ArrayField
                record={record}
                source="emailRecipients"
                sortable={false}
                label="resources.admin-notifications.labels.emailRecipients"
                displayedElementsCount={2}
                emptyArrayDisplay={translate(
                  "resources.admin-notifications.labels.noEmailRecipients"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Phone number">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <SmsIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="smsRecipients">
              <ArrayField
                record={record}
                source="smsRecipients"
                sortable={false}
                label="resources.admin-notifications.labels.smsRecipients"
                displayedElementsCount={2}
                emptyArrayDisplay={translate(
                  "resources.admin-notifications.labels.noSmsRecipients"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="content-container">
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={NotificationsOutlinedIcon} />}
          title="resources.admin-notifications.name"
          actions={<ListActions />}
          bulkActionButtons={false}
          filters={<ListFilter />}
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType="edit"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                secondaryText={(record) => (
                  <RenderSecondaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      {...props}
                      record={record}
                      editable={() =>
                        permissions &&
                        permissions[permissionNames.admin.notifications.main] &&
                        permissions[permissionNames.admin.notifications.update]
                      }
                      canActivateOrDeactivate={() =>
                        permissions &&
                        permissions[permissionNames.admin.notifications.main] &&
                        permissions[permissionNames.admin.notifications.update]
                      }
                    >
                      {permissions &&
                        permissions[permissionNames.admin.notifications.main] &&
                        permissions[
                          permissionNames.admin.notifications.delete
                        ] && <DeleteRecordMenuItem />}
                    </ActionsField>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <ProtectedElement source="name">
                  <TextField
                    source="name"
                    label="resources.admin-notifications.labels.name"
                  />
                </ProtectedElement>
                <ProtectedElement source="emailRecipients">
                  <ArrayField
                    source="emailRecipients"
                    sortable={false}
                    label="resources.admin-notifications.labels.emailRecipients"
                    displayedElementsCount={2}
                    emptyArrayDisplay={translate(
                      "resources.admin-notifications.labels.noEmailRecipients"
                    )}
                  />
                </ProtectedElement>
                <ProtectedElement source="smsRecipients">
                  <ArrayField
                    source="smsRecipients"
                    sortable={false}
                    label="resources.admin-notifications.labels.smsRecipients"
                    displayedElementsCount={2}
                    emptyArrayDisplay={translate(
                      "resources.admin-notifications.labels.noSmsRecipients"
                    )}
                  />
                </ProtectedElement>
                <FunctionField<NotificationSubscriptionListDto & Record>
                  source="status"
                  headerClassName="centered-content"
                  label="resources.common.labels.active"
                  render={(record) => (
                    <Box className="centered-content">
                      {record?.status === "Active" ? (
                        <CheckBoxIcon
                          className={record.status}
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                      ) : (
                        <IndeterminateCheckBoxIcon
                          className={record?.status}
                          style={{
                            color: theme.palette.error.main,
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
                <ActionsField
                  editable={() =>
                    permissions &&
                    permissions[permissionNames.admin.notifications.main] &&
                    permissions[permissionNames.admin.notifications.update]
                  }
                  canActivateOrDeactivate={() =>
                    permissions &&
                    permissions[permissionNames.admin.notifications.main] &&
                    permissions[permissionNames.admin.notifications.update]
                  }
                >
                  {permissions &&
                    permissions[permissionNames.admin.notifications.main] &&
                    permissions[permissionNames.admin.notifications.delete] && (
                      <DeleteRecordMenuItem />
                    )}
                </ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
